import { useCallback } from "react";
import { toast } from "react-toastify";
import {
  CopropertyDto,
  TerrainEstimationDto,
  UpdateCopropertyBuildingsCommand,
  UpdateCopropertyClaimsHistoryCommand,
  UpdateCopropertyCommand,
  UpdateCopropertyComplementsCommand,
  UpdateCopropertyResponse,
} from "api/coproperty";
import { CoproRegistryEntryDto } from "api/referential";
import { copropertiesApi } from "services/swagger-api";
import { useAsyncResource } from "back-office/shared/hooks/useAsyncResource";
import { useRiskData, useSubscriptionProcessState } from "../contexts/selectors/selectors";
import { useRefreshSubscriptionProcess } from "../hooks/useRefreshSubscriptionProcess";

/** Adresse minimale nécessaire à la création d'une copropriété. */
export interface CopropertyAddressInput {
  line1: string;
  zipCode: string;
  city: string;
  country?: string;
  formattedAddress?: string | null;
  /** Point de la copropriété (registre ou Google) : alimente l'estimation du terrain. */
  latitude?: number | null;
  longitude?: number | null;
  googlePlaceId?: string | null;
}

/**
 * Données et mutations de l'étape copropriété du funnel MRI.
 *
 * L'id de la copropriété rattachée vient du process (riskData.copropertyId) ; la fiche
 * complète (adresse + bâtiments) est chargée à part. Toute mutation qui complète la
 * sous-étape rafraîchit le process pour faire avancer le stepper.
 */
export const useCoproperty = () => {
  const { riskId } = useSubscriptionProcessState();
  // person est le ReadAssureDto polymorphe du backend : plat, discriminé par `type`
  // (morale/physique), le SIRET étant sérialisé `registrationNumber`.
  const riskData = useRiskData() as {
    copropertyId?: string;
    person?: { type?: string; registrationNumber?: string };
  } | null;
  const copropertyId = riskData?.copropertyId ?? null;
  /** SIRET du représentant (personne morale) : pré-remplissage des copros du syndic. */
  const syndicSiret =
    riskData?.person?.type === "morale"
      ? riskData.person.registrationNumber?.replace(/\s/g, "") ?? null
      : null;
  const { refresh } = useRefreshSubscriptionProcess();

  const fetchCoproperty = useCallback(
    () => copropertiesApi.getCoproperty({ copropertyId: copropertyId! }),
    [copropertyId]
  );
  const {
    data: coproperty,
    isLoading,
    error,
    reload,
  } = useAsyncResource<CopropertyDto>(fetchCoproperty, copropertyId != null);

  /** Crée la copropriété puis la rattache au risque MRI — depuis le registre ou une adresse saisie. */
  const createAndAttach = useCallback(
    async (address: CopropertyAddressInput, name?: string | null, registrationNumber?: string | null) => {
      if (!riskId) return false;

      const created = await copropertiesApi.createCoproperty({
        createCopropertyCommand: {
          address: {
            line1: address.line1,
            zipCode: address.zipCode,
            city: address.city,
            country: address.country || "FR",
            formattedAddress: address.formattedAddress ?? undefined,
            latitude: address.latitude ?? undefined,
            longitude: address.longitude ?? undefined,
            googlePlaceId: address.googlePlaceId ?? undefined,
          },
          name: name ?? undefined,
          registrationNumber: registrationNumber ?? undefined,
        },
      });

      if (!created.isSuccess || !created.id) {
        toast.error(created.errorMessage || "La création de la copropriété a échoué.");
        return false;
      }

      const linked = await copropertiesApi.linkCopropertyToRisk({
        linkCopropertyToRiskRequest: { riskId, copropertyId: created.id },
      });

      if (!linked.isSuccess) {
        toast.error(linked.errorMessage || "Le rattachement de la copropriété a échoué.");
        return false;
      }

      await refresh();
      return true;
    },
    [riskId, refresh]
  );

  /** Crée directement depuis une entrée du registre national. */
  const createFromRegistry = useCallback(
    async (entry: CoproRegistryEntryDto) => {
      const line1 = entry.numeroVoie || entry.adresse;
      if (!line1 || !entry.codePostal || !entry.commune) {
        toast.error("L'adresse de cette copropriété du registre est incomplète — créez-la manuellement.");
        return false;
      }

      return createAndAttach(
        {
          line1,
          zipCode: entry.codePostal,
          city: entry.commune,
          formattedAddress: entry.adresse,
          latitude: entry.latitude,
          longitude: entry.longitude,
        },
        entry.nomCopropriete,
        entry.numeroImmatriculation
      );
    },
    [createAndAttach]
  );

  /**
   * Enregistre les attributs de la copropriété (sous-étape copropriété). La réponse porte
   * le verdict d'éligibilité de la sous-étape ; null si l'enregistrement a échoué.
   */
  const updateCoproperty = useCallback(
    async (command: UpdateCopropertyCommand): Promise<UpdateCopropertyResponse | null> => {
      if (!copropertyId) return null;

      const response = await copropertiesApi.updateCoproperty({
        copropertyId,
        updateCopropertyCommand: command,
      });

      if (!response.isSuccess) {
        toast.error(response.errorMessage || "L'enregistrement de la copropriété a échoué.");
        return null;
      }

      await reload();
      return response;
    },
    [copropertyId, reload]
  );

  /** Remplace la liste des bâtiments (sous-étape bâtiments) ; même contrat de réponse. */
  const updateBuildings = useCallback(
    async (command: UpdateCopropertyBuildingsCommand): Promise<UpdateCopropertyResponse | null> => {
      if (!copropertyId) return null;

      const response = await copropertiesApi.updateCopropertyBuildings({
        copropertyId,
        updateCopropertyBuildingsCommand: command,
      });

      if (!response.isSuccess) {
        toast.error(response.errorMessage || "L'enregistrement des bâtiments a échoué.");
        return null;
      }

      await reload();
      return response;
    },
    [copropertyId, reload]
  );

  /** Compléments (contrat en cours, AG) — sous-étape passable, pas de verdict. */
  const updateComplements = useCallback(
    async (command: UpdateCopropertyComplementsCommand): Promise<boolean> => {
      if (!copropertyId) return false;

      const response = await copropertiesApi.updateCopropertyComplements({
        copropertyId,
        updateCopropertyComplementsCommand: command,
      });

      if (!response.isSuccess) {
        toast.error(response.errorMessage || "L'enregistrement des compléments a échoué.");
        return false;
      }

      await reload();
      return true;
    },
    [copropertyId, reload]
  );

  /**
   * Sinistralité déclarative — période, garanties touchées, historique d'assurance et
   * renonciation. La réponse porte le verdict d'éligibilité global ; null si l'enregistrement a échoué.
   */
  const updateClaimsHistory = useCallback(
    async (command: UpdateCopropertyClaimsHistoryCommand): Promise<UpdateCopropertyResponse | null> => {
      if (!copropertyId) return null;

      const response = await copropertiesApi.updateCopropertyClaimsHistory({
        copropertyId,
        updateCopropertyClaimsHistoryCommand: command,
      });

      if (!response.isSuccess) {
        toast.error(response.errorMessage || "L'enregistrement de la sinistralité a échoué.");
        return null;
      }

      await reload();
      return response;
    },
    [copropertyId, reload]
  );

  /**
   * Estimation du terrain depuis le point de la copropriété (altitude, parcelle, bâti,
   * espace vert). Best-effort : null si l'appel échoue, la fiche reste saisissable.
   */
  const estimateTerrain = useCallback(async (): Promise<TerrainEstimationDto | null> => {
    if (!copropertyId) return null;
    try {
      return await copropertiesApi.getCopropertyTerrainEstimation({ copropertyId });
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [copropertyId]);

  return {
    riskId,
    copropertyId,
    syndicSiret,
    coproperty,
    isLoading,
    error,
    reload,
    createAndAttach,
    createFromRegistry,
    updateCoproperty,
    updateBuildings,
    updateComplements,
    updateClaimsHistory,
    estimateTerrain,
  };
};
